import { Order, TableData } from "@/dto/tableData";
import sendItemsToKitchen from "@/dataLayer/sendItemsToKitchen";
import { CheckoutItemModifier } from "@/dto/checkoutInfo";
import { Modifier } from "@/dto/modifier";
import { RetailCartItem } from "@/dto/retailCartItem";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { OrderType, orderTypes } from "@/types";
import { clearCart } from "./cartUtils";
import { saveToLocalStorage } from "./localStorageUtils";
import { Dispatch, SetStateAction } from "react";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { RetailCartValue } from "@/dto/retailCartValue";
import { Merchant } from "@/dto/merchant";
import { LocalStorageKey } from "./storageKeys";
import getMenu from "@/dataLayer/getMenu";

export const convertToScanToOrderInfo = (
  orderType: string | string[] | undefined,
  tableId: string | string[] | undefined,
  employeeId?: string | string[] | undefined
): ScanToOrderInfo | null => {
  let scanToOrderInfo = null;
  const normalizedOrderType = Array.isArray(orderType)
    ? orderType[0]
    : orderType;
  const normalizedTableId = Array.isArray(tableId) ? tableId[0] : tableId;
  const normalizedEmployeeId = Array.isArray(employeeId)
    ? employeeId[0]
    : employeeId;

  if (
    normalizedOrderType &&
    typeof normalizedOrderType === "string" &&
    isValidOrderType(normalizedOrderType.toLowerCase()) &&
    normalizedTableId &&
    typeof normalizedTableId === "string" &&
    (!normalizedEmployeeId || typeof normalizedEmployeeId === "string")
  ) {
    scanToOrderInfo = {
      orderType: normalizedOrderType.toLowerCase() as OrderType,
      tableId: normalizedTableId,
      ...(normalizedEmployeeId && { employeeId: normalizedEmployeeId }),
    };
  }
  return scanToOrderInfo;
};

export const isValidOrderType = (value: string): value is OrderType => {
  return orderTypes.some((type) => type.toLowerCase() === value);
};

export const getCartButtonText = (
  cartCount: number,
  isRunningTabsEnabled: boolean
): string => {
  let text = "";
  if (isRunningTabsEnabled) {
    switch (cartCount) {
      case 0:
        text = "Close out / Pay";
        break;
      case 1:
        text = "Send item to the kitchen";
        break;
      default:
        text = `Send ${cartCount} items to the kitchen`;
        break;
    }
    return text;
  } else {
    return "Continue to checkout";
  }
};

export const sendToKitchen = async (
  merchant: Merchant,
  recaptcha: string,
  cartItems: RetailCartItem[],
  specialInstructions: string,
  tableId: string,
  merchantId: string,
  tableData: TableData,
  onRequestClose: () => void,
  setIsSentToKitchen: Dispatch<SetStateAction<boolean>>,
  setFailedToSendToKitchen: Dispatch<SetStateAction<boolean>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  employeeId?: string,
  menuId?: string
) => {
  const items = cartItems.flatMap((item: RetailCartItem) => {
    const {
      id,
      modifiers,
      isAgeRestricted,
      specialInstructions,
      quantity,
      menuId,
    } = item.value;
    const checkoutItemModifiers: CheckoutItemModifier[] = modifiers
      .filter((modifier) => modifier.id !== undefined)
      .flatMap((modifier: Modifier) =>
        Array.from({ length: modifier.quantity ?? 1 }, () => ({
          id: modifier.id!,
        }))
      );
    const cartItem = {
      id,
      itemModifiers: checkoutItemModifiers,
      isAgeRestricted: isAgeRestricted ?? false,
      note: specialInstructions,
      ...(menuId && { menuId }),
    };
    return Array.from({ length: quantity || 1 }, () => cartItem);
  });
  const payload = {
    cart: { giftCards: [], items: items },
    note: specialInstructions,
    typeMetadata: {
      tableUuid: tableId,
      employeeUuid: employeeId ? employeeId : "",
    },
  };
  try {
    const data = await sendItemsToKitchen(
      merchantId,
      tableId,
      recaptcha,
      payload,
      menuId
    );

    if (data) {
      const newTableData = { ...tableData, order: data };
      clearCart();
      saveToLocalStorage(LocalStorageKey.TABLE_DATA, newTableData);
      setIsLoading(false);
      onRequestClose();
      setIsSentToKitchen(true);
    }
  } catch (error) {
    console.error(`${merchant.slug} - Error in sendToKitchen: `, error);
    setFailedToSendToKitchen(true);
  }
};

export const goToGuestCheckout = (
  router: AppRouterInstance,
  scanToOrderInfo?: ScanToOrderInfo
) => {
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  router.push("/guest-checkout" + queryParams);
};

export const formatOrderTypeToString = (input: string): string => {
  if (!input) {
    return "";
  }

  const firstCharUpper = input.charAt(0).toUpperCase();
  const restOfString = input.slice(1);
  const stringWithHyphens = restOfString.replace(/_/g, "-");

  return `${firstCharUpper}${stringWithHyphens}`;
};

export function buildScanToOrderSearchParams({
  orderType,
  tableId,
  employeeId,
}: Partial<ScanToOrderInfo>): string {
  const params = new URLSearchParams();
  if (orderType) {
    params.append("order_type", String(orderType));
  }
  if (tableId) {
    params.append("table_id", String(tableId));
  }
  if (employeeId) {
    params.append("employee_id", String(employeeId));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

export const convertTableDataToRetailCartItems = (
  tableData: TableData
): RetailCartItem[] => {
  if (
    !tableData ||
    !tableData.order ||
    !tableData.order.cart ||
    !tableData.order.cart.items
  ) {
    return [];
  }

  const retailCartItems: RetailCartItem[] = tableData.order.cart.items.map(
    (item: RetailCartValue) => {
      return {
        key: item.id,
        value: item,
      };
    }
  );

  return retailCartItems;
};

export const processScanToOrderCartItems = async (
  order: Order,
  merchantId: string,
  menuId?: string
) => {
  // search for item name using item.id in menu and add to item
  const menu = await getMenu(merchantId, OrderType.PICKUP, menuId);
  const menuItems = Object.fromEntries(
    menu.items.map((item) => [item.id, item])
  );

  // rename note to special instructions
  const updatedItems = order?.cart?.items.map((item) => {
    const itemWithName = {
      ...item,
      name: item.name ?? menuItems[item.id]?.name,
    };
    if (itemWithName.note === undefined) return itemWithName;
    const { note, ...rest } = itemWithName;
    return { ...rest, specialInstructions: note };
  });

  return {
    ...order,
    cart: {
      ...order.cart,
      items: updatedItems,
    },
  };
};
